import { observer } from "mobx-react";
import { OFTableController } from "../OFTableController";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const RenderCellContent = observer(
  ({
    columnIndex,
    rowIndex,
    ofTableController,
  }: {
    columnIndex: number;
    rowIndex: number;
    ofTableController: OFTableController;
  }) => {
    const getRowData = () => {
      const { sortedFilteredList, getRowPointers } = ofTableController;

      // If the rendered rows were counted before the pointers - ignore
      if (rowIndex >= getRowPointers.length) {
        return null;
      }

      const pointer = getRowPointers[rowIndex].index;
      const rowData = sortedFilteredList[pointer];

      return rowData;
    };

    const selectedRow = () => {
      const { multiSelectedRows, selectedRow } = ofTableController;

      return multiSelectedRows.rows[rowData._id] || selectedRow.row === rowData._id;
    };

    const isCellEditable = () => {
      const { currentEditableCell, visibleColumns, props, getRowPointers } = ofTableController;

      if (!props.editableCells) {
        return false;
      }

      const column = visibleColumns[columnIndex];
      const cellLocation = getRowPointers[rowIndex].index.toString() + columnIndex.toString();

      if (column.editCell && currentEditableCell && currentEditableCell === cellLocation) {
        return true;
      }

      return false;
    };

    const getOffset = () => {
      const { visibleColumns, fixedColumns } = ofTableController;

      let totalOffset = 0;
      let fixedOffset = 0;

      visibleColumns.forEach((col, index) => {
        if (col.subColumns) {
          col.subColumns.forEach((subCol, subIndex) => {
            if (fixedColumns < subIndex + 1 && subIndex <= columnIndex) {
              totalOffset += subCol.width;
            }
            if (fixedColumns >= subIndex + 1 && subIndex < columnIndex) {
              fixedOffset += subCol.width;
            }
          });
        } else {
          if (fixedColumns < index + 1 && index <= columnIndex) {
            totalOffset += col.width;
          }

          if (fixedColumns >= index + 1 && index < columnIndex) {
            fixedOffset += col.width;
          }
        }
      });

      if (fixedColumns >= columnIndex + 1) {
        return fixedOffset;
      } else {
        return totalOffset;
      }
    };

    const { visibleColumns, props, offsetLeft, tableWidth, tableRowHeight, fixedColumns } = ofTableController;

    const column = visibleColumns[columnIndex];
    const rowData: any = getRowData();

    if (!rowData || (column.id === "OFTable_lastCol" && column.width === 0)) {
      return null;
    }

    let isFixed = false;
    const offset = getOffset();

    if (fixedColumns >= columnIndex + 1) {
      isFixed = true;
    } else if (offset < offsetLeft) {
      return <span key={column.id} className="cell" style={{ height: tableRowHeight, width: column.width + "px" }} />;
    }

    if (offset - column.width > tableWidth + offsetLeft) {
      return null;
    }

    let additionalClass = "cell";
    let cellAlign = "text-center";

    const additionalProps = {};

    if (props.onRowClick && column.id !== "checkbox") {
      additionalProps["onClick"] = () => ofTableController.handleRowClick(rowData);
    }

    if (props.contextMenuObj) {
      additionalProps["onContextMenu"] = (event) => ofTableController.handleRightClick({ event, rowData: rowData });
    }

    if (ofTableController.props?.editableCells && column.id !== "checkbox") {
      additionalProps["onDoubleClick"] = () => ofTableController.handleCellEdit(rowIndex, columnIndex);
    }

    if (column.align) {
      cellAlign = column.align === "left" ? "text-left" : "text-right";
    }

    if (props.enableCellHighlighting) {
      additionalClass = "cell";
    }

    if (!column.animated) {
      additionalClass += " noAnimation";
    }

    if (ofTableController.pinnedRows[rowData._id]) {
      additionalClass += " pinned";
    }

    if (selectedRow()) {
      additionalClass += " selected";
    }

    if (isFixed) {
      additionalClass += " fixedCol";
    }

    if (fixedColumns === columnIndex + 1) {
      additionalClass += " lastFixed";
    }

    return (
      <div
        {...additionalProps}
        style={{
          height: ofTableController.tableRowHeight,
          width: column.width + "px",
          left: isFixed ? offset : "unset",
        }}
        data-e2e={column.id}
        className={additionalClass}
      >
        <div className={"w-full " + cellAlign}>
          {isCellEditable()
            ? column.editCell(rowData, column.accessor(rowData))
            : column.Cell(rowData, column.accessor(rowData))}
        </div>
        {isCellEditable() && (
          <FontAwesomeIcon
            icon={faCircleXmark}
            className="closeEdit"
            onClick={() => ofTableController.handleCellEdit()}
          />
        )}
      </div>
    );
  }
);

export { RenderCellContent };
